// src/components/forms/MyFormContainer.tsx
import React from 'react';
import { Container, Box, useTheme, useMediaQuery } from '@mui/material';

interface MyFormContainerProps {
  children: React.ReactNode;
  /**
   * Ancho máximo del contenedor
   * - 'sm': 600px
   * - 'md': 900px  
   * - 'lg': 1200px
   * - 'xl': 1536px
   * - false: sin límite
   * @default 'lg'
   */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | false;
  /**
   * Si true, el contenedor ocupa todo el ancho disponible
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Padding personalizado para el contenedor
   */
  padding?: number | string;
  /**
   * Si true, no aplica padding en móvil
   * @default true
   */
  disableGuttersOnMobile?: boolean;
}

export const MyFormContainer: React.FC<MyFormContainerProps> = ({
  children,
  maxWidth = 'lg',
  fullWidth = false,
  padding,
  disableGuttersOnMobile = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Determinar el padding basado en el tamaño de pantalla
  const defaultPadding = {
    xs: disableGuttersOnMobile ? 1 : 2,  // Mínimo padding en móvil
    sm: 3,                                // Padding medio en tablet
    md: 4,                                // Padding generoso en desktop
    lg: 5,                                // Padding extra en pantallas grandes
  };

  const finalPadding = padding !== undefined ? padding : defaultPadding;

  return (
    <Container
      maxWidth={fullWidth ? false : maxWidth}
      disableGutters={disableGuttersOnMobile && isMobile}
      sx={{
        px: finalPadding,
        py: { 
          xs: 1,  // Padding vertical en móvil
          sm: 1,  // Padding vertical en tablet
          md: 1   // Padding vertical en desktop
        },
        minHeight: {
          xs: 'calc(100vh - 120px)',
          sm: 'calc(100vh - 140px)',
          md: 'calc(100vh - 155px)'
        },
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        
      }}
    >
      <Box
        sx={{
          width: '100%',
          backgroundColor: 'transparent',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          

          ...(fullWidth && { width: '100%' }),

          ...(maxWidth && !fullWidth && {
            '& > div': {
              maxWidth: '100%',
              mx: 'auto'
            }
          }),
         
          '& > *': {
            width: '100%',
          }
        }}
      >
        {children}
      </Box>
    </Container>
  );
};

export const FormContainer: React.FC<MyFormContainerProps> = ({
  children,
  maxWidth = 'lg',
  ...props
}) => {
  return (
    <MyFormContainer maxWidth={maxWidth} {...props}>
      <Box>
        {children}
      </Box>
    </MyFormContainer>
  );
};

export default MyFormContainer;